import { Box, Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { arrowSxDefault, tooltipSxDefault } from "./Styles";

/**
 * @author Jhovanny Fragoso Marquez
 * @jobTitle Frontend Developer
 * @email jfragoso@ibl.mx
 *
 * @component CustomTooltip
 * @description
 * A reusable tooltip component that displays a tooltip when the text is truncated
 * or always wraps its children depending on the provided props.
 *
 * @param {Object} props - Component props.
 * @param {string} props.text - Text to display inside the tooltip.
 * @param {React.ReactNode} [props.children=null] - Optional child element to wrap with the tooltip.
 *                                                  If not provided, a `<span>` with the text is rendered.
 * @param {("bottom"|"top"|"left"|"right")} [props.placement="bottom"] - Tooltip position relative to the element.
 * @param {boolean} [props.arrow=false] - Whether to display an arrow on the tooltip.
 * @param {Object} [props.tooltipSx=tooltipSxDefault] - Custom `sx` styles for the tooltip container.
 * @param {Object} [props.arrowSx=arrowSxDefault] - Custom `sx` styles for the tooltip arrow.
 * @param {string} [props.classes=""] - Optional CSS classes applied to the fallback `<span>` element.
 * @param {string} [props.tooltipClasses=""] - Optional CSS classes applied to the fallback `<Tooltip>` element.
 * @param {boolean} [props.showIfTruncated=false] - Whether to only show the tooltip if the text is visually truncated.
 * @param {JSX.Element} [props.icon=null] - Icon element to display before the text inside the span. Useful for adding visual indicators next to the text (optional).
 * @returns {JSX.Element} A JSX element with or without a tooltip based on conditions.
 */
const CustomTooltip = ({
    text,
    children = null,
    placement = "bottom",
    arrow = false,
    tooltipSx = tooltipSxDefault,
    arrowSx = arrowSxDefault,
    classes = "",
    tooltipClasses="",
    showIfTruncated = false,
    icon = null
}) => {
    const ref = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        if (!children && ref.current) {
            const el = ref.current;
            const clampedHeight = el.clientHeight;
            const clampedWidth = el.clientWidth;

            const prevOverflow = el.style.overflow;
            const prevWebkitLineClamp = el.style.webkitLineClamp;
            const prevDisplay = el.style.display;

            el.style.overflow = 'visible';
            el.style.webkitLineClamp = 'unset';
            el.style.display = 'block';

            const naturalHeight = el.scrollHeight;
            const naturalWidth = el.scrollWidth;

            el.style.overflow = prevOverflow;
            el.style.webkitLineClamp = prevWebkitLineClamp;
            el.style.display = prevDisplay;

            setIsTruncated(naturalWidth > clampedWidth || naturalHeight > clampedHeight);
        }
    }, [text, children]);

    const wrapWithTooltip = (node) => (
        <Tooltip
            title={text}
            placement={placement}
            arrow={arrow}
            componentsProps={{
                tooltip: { sx: tooltipSx },
                arrow: { sx: arrowSx },
            }}
            className={`cursor-pointer ${tooltipClasses}`}
        >
            {node}
        </Tooltip>
    );

    if (!showIfTruncated && children) return wrapWithTooltip(children);

    const span = <span ref={ref} className={classes}>{icon ? icon : ''}{ text }</span>;
    return showIfTruncated && isTruncated ? wrapWithTooltip(<Box component="span">{span}</Box>) : span;
};

export default CustomTooltip;
